import { useState } from 'react';
import type { Icon } from '@phosphor-icons/react';
import {
  BowlFood,
  Cookie,
  Drop,
  Eyedropper,
  Heart,
  MoonStars,
  Note,
  PawPrint,
  Sun,
  SunHorizon,
  Warning,
} from '@phosphor-icons/react';
import { useNow } from '../../data/kioskRefresh';
import { FlipClock } from './widgets/ClockWidget';

// Pet-care lock screen — the second kiosk lock variant. Shown when a device is
// locked with lockVariant === 'petcare' (e.g. while a sitter is staying). The
// content is transcribed from the household's printed feeding sheet; the layout
// is three stacked time-of-day blocks (Morning / Afternoon / Evening), each
// split into a time column + one column per pet. The current block stays bright
// with a glowing green border while the others dim, so a sitter's eye lands on
// "what's now".
//
// Each pet (Nevae, Stella) shows a circular photo + name so a sitter can scan a
// column at a glance.

type ItemType = 'water' | 'meal' | 'snack' | 'meds';

interface CareItem {
  type: ItemType;
  /** Semibold lead label, e.g. "Water" / "Breakfast" / "Medicine". */
  label: string;
  /** Sub-instructions rendered as a bulleted list under the label. */
  parts: string[];
}

interface PetMeta {
  name: string;
  /** Photo served from public/pets — falls back to a monogram until present. */
  photo: string;
  initial: string;
}

const PETS: Record<'nevae' | 'stella', PetMeta> = {
  nevae: { name: 'Nevae', photo: '/pets/nevae.jpg', initial: 'N' },
  stella: { name: 'Stella', photo: '/pets/stella.jpg', initial: 'S' },
};

type PeriodKey = 'morning' | 'afternoon' | 'evening';

interface Period {
  key: PeriodKey;
  label: string;
  Icon: Icon;
  nevae: CareItem[];
  stella: CareItem[];
}

// Faithful transcription of the printed sheet. Wording kept close to the
// original so a sitter following the paper and the screen never sees a
// mismatch; only formatting (½, ⅙, en-dashes) is tidied.
const SCHEDULE: Period[] = [
  {
    key: 'morning',
    label: 'Morning',
    Icon: SunHorizon,
    nevae: [
      { type: 'water', label: 'Water', parts: ['Fill up water dish (pre-boiled water)'] },
      { type: 'meal', label: 'Breakfast', parts: ['1 scoop of kibble', '⅙ of a package of soft food'] },
      { type: 'meds', label: 'Medicine', parts: ['1 eye drop in each eye'] },
    ],
    stella: [
      { type: 'water', label: 'Water', parts: ['Fill up water dish (tap water)'] },
      { type: 'meal', label: 'Breakfast', parts: ['1 light scoop of kibble', '½ cup of broth in fridge'] },
    ],
  },
  {
    key: 'afternoon',
    label: 'Afternoon',
    Icon: Sun,
    nevae: [
      { type: 'water', label: 'Water', parts: ['Fill up water dish (pre-boiled water)'] },
      { type: 'snack', label: 'Snack', parts: ['1 peanut butter soft chew (from his basket)'] },
    ],
    stella: [
      { type: 'water', label: 'Water', parts: ['Fill up water dish (tap water)'] },
      { type: 'snack', label: 'Snack', parts: ['1 mozzarella cheese stick (in bottom drawer of fridge)'] },
    ],
  },
  {
    key: 'evening',
    label: 'Evening',
    Icon: MoonStars,
    nevae: [
      { type: 'water', label: 'Water', parts: ['Fill up water dish (pre-boiled water)'] },
      {
        type: 'meal',
        label: 'Dinner',
        parts: [
          '1 cup of kibble',
          '1 pump of fish oil',
          '3 tbsp of veggies in fridge (heat ~30 sec or until warm)',
          '4 lamb freeze-dried mixers',
        ],
      },
      { type: 'meds', label: 'Medicine', parts: ['1 eye drop in each eye'] },
    ],
    stella: [
      { type: 'water', label: 'Water', parts: ['Fill up water dish (tap water)'] },
      {
        type: 'meal',
        label: 'Dinner',
        parts: [
          'Food from plastic container in freezer (heat ~2 min or until warm)',
          '3 pumps of fish oil',
          '4 dried anchovies',
        ],
      },
    ],
  },
];

const TYPE_ICON: Record<ItemType, Icon> = {
  water: Drop,
  meal: BowlFood,
  snack: Cookie,
  meds: Eyedropper,
};

// Per-type icon tint — a distinct hue per kind so the row type reads at a
// glance. Green is reserved for the "now" highlight, so it's avoided here.
// Inactive blocks dim the whole thing.
const TYPE_ICON_COLOR: Record<ItemType, string> = {
  water: 'text-sky-light',
  meal: 'text-rose-light',
  snack: 'text-rose-light',
  meds: 'text-amber-light',
};

// Which timeline block is "now". Morning 12:00a–10:59a, Afternoon 11:00a–3:59p,
// Evening 4:00p–11:59p.
function currentPeriod(hour: number): PeriodKey {
  if (hour < 11) return 'morning';
  if (hour < 16) return 'afternoon';
  return 'evening';
}

// Circular pet photo; falls back to a neutral monogram if the image is missing
// (so the screen is never broken before photos land).
function PetAvatar({ pet, size = 64 }: { pet: PetMeta; size?: number }) {
  const [failed, setFailed] = useState(false);
  return (
    <span
      className="relative flex-none overflow-hidden rounded-full bg-subtle"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {failed ? (
        <span className="flex h-full w-full items-center justify-center text-lg font-semibold text-fg-secondary">
          {pet.initial}
        </span>
      ) : (
        <img
          src={pet.photo}
          alt=""
          className="h-full w-full object-cover"
          style={{ objectPosition: '50% 28%' }}
          onError={() => setFailed(true)}
        />
      )}
    </span>
  );
}

function CareRow({ item }: { item: CareItem }) {
  const RowIcon = TYPE_ICON[item.type];
  const isMeds = item.type === 'meds';

  return (
    <li className="flex items-start gap-3">
      <RowIcon
        size={22}
        weight={isMeds ? 'fill' : 'regular'}
        className={`mt-0.5 flex-none ${TYPE_ICON_COLOR[item.type]}`}
        aria-hidden="true"
      />
      <div className="text-lg leading-snug text-fg">
        <span className="font-semibold text-fg">{item.label}</span>
        <ul className="mt-1 list-disc space-y-1 pl-5 marker:text-fg-tertiary">
          {item.parts.map((part, i) => (
            <li key={i}>{part}</li>
          ))}
        </ul>
      </div>
    </li>
  );
}

// A pet's photo + name. Lives in the shared header row of the block grid so
// both pets' headers stay aligned regardless of how many items each has.
function PetHeader({ pet }: { pet: PetMeta }) {
  return (
    <div className="flex items-center gap-3 px-6">
      <PetAvatar pet={pet} size={48} />
      <h3 className="text-xl font-semibold tracking-tight text-fg">{pet.name}</h3>
    </div>
  );
}

function PetItems({ items }: { items: CareItem[] }) {
  return (
    <ul className="flex flex-col gap-3 px-6">
      {items.map((item, i) => (
        <CareRow key={i} item={item} />
      ))}
    </ul>
  );
}

export function PetCareLockScreen() {
  const now = useNow(60_000);
  const activeKey = currentPeriod(now.getHours());

  return (
    <div
      className="kiosk-shell fixed inset-0 z-50 flex flex-col gap-6 overflow-y-auto bg-canvas px-8 py-8 text-fg"
      role="status"
      aria-live="polite"
    >
      {/* Header — title + paw on the left, the dashboard's flip clock on the
          right so a glance still tells time. */}
      <header className="flex items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <span className="flex-none rounded-2xl bg-green-soft p-3 text-green">
            <PawPrint size={40} weight="fill" aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-4xl font-semibold tracking-tight">Pet Care</h1>
            <p className="text-base text-fg-secondary">Daily schedule while we're away</p>
          </div>
        </div>
        <FlipClock size="default" className="flex-none" />
      </header>

      {/* Time-of-day blocks. Each is a full-width panel split into a time
          column + one column per pet. The current block keeps a glowing green
          border at full brightness; the others dim back so the eye lands on
          "what's now". */}
      <div className="flex flex-1 flex-col gap-5">
        {SCHEDULE.map((period) => {
          const isNow = period.key === activeKey;
          return (
            <section
              key={period.key}
              className={`flex flex-1 overflow-hidden rounded-3xl border transition-opacity ${
                isNow ? 'border-green bg-green-soft glow-brand' : 'border-border bg-elevated opacity-45'
              }`}
            >
              {/* Time column — icon, label, and a Now pill for the current block. */}
              <div className="flex w-52 flex-none flex-col items-center justify-center gap-3 border-r border-border p-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-border-strong">
                  <period.Icon
                    size={28}
                    weight="light"
                    className={isNow ? 'text-fg' : 'text-fg-secondary'}
                    aria-hidden="true"
                  />
                </span>
                <span
                  className={`text-sm font-semibold uppercase tracking-[0.2em] ${
                    isNow ? 'text-fg' : 'text-fg-secondary'
                  }`}
                >
                  {period.label}
                </span>
                {isNow && (
                  <span className="rounded-full border border-green px-2.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-green">
                    Now
                  </span>
                )}
              </div>

              {/* Both pets share a 2-row grid (headers row + items row) so the
                  photos/names always align horizontally even when one pet has
                  more items. content-center keeps the group vertically centered;
                  a full-height rule splits the two. Stella left, Nevae right. */}
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-1/2 w-px bg-border" aria-hidden="true" />
                <div className="grid h-full grid-cols-2 content-center gap-y-4 py-6">
                  <PetHeader pet={PETS.stella} />
                  <PetHeader pet={PETS.nevae} />
                  <PetItems items={period.stella} />
                  <PetItems items={period.nevae} />
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* Allergy warning */}
      <div
        className="flex items-start gap-3 border-l-4 border-rose-light py-8 pl-8 pr-6"
        style={{
          backgroundImage:
            'linear-gradient(to right, color-mix(in srgb, var(--berg-rose-light) 14%, transparent), transparent 55%)',
        }}
      >
        <Warning size={28} weight="fill" className="mt-0.5 flex-none text-rose-light" aria-hidden="true" />
        <div className="text-lg leading-snug text-fg-primary mt-0.75">
          <p><span className="font-semibold text-rose-light mr-2">Reminder:</span> Nevae is allergic to most animal products except lamb and fish oil.</p>
        </div>
      </div>

      {/* Notes callout */}
      <div
        className="flex items-start gap-3 border-l-4 border-green py-8 pl-8 pr-6"
        style={{
          backgroundImage:
            'linear-gradient(to right, color-mix(in srgb, var(--berg-green) 14%, transparent), transparent 55%)',
        }}
      >
        <Note size={28} weight="fill" className="mt-0.5 flex-none text-green" aria-hidden="true" />
        <div className="text-lg leading-snug text-fg-primary mt-0.75">
          <p><span className="font-semibold text-green mr-2">Note:</span> Feel free to give them other snacks from their baskets if it feels right.</p>
        </div>
      </div>

      {/* Footer — gratitude, mirroring the sheet's sign-off. */}
      <footer className="flex items-center justify-center gap-2 pt-1 text-fg-tertiary">
        <Heart size={18} weight="fill" className="text-green" aria-hidden="true" />
        <span className="text-sm uppercase tracking-widest">Thank you</span>
      </footer>
    </div>
  );
}
